/**
 * Batch Generation API
 *
 * API client for Gemini Batch API integration.
 * Endpoints: /api/v1/batch/*
 */

import { apiClient } from '../api-client';

// ============================================================================
// Types
// ============================================================================

export interface BatchConfig {
  enabled: boolean;
  max_images: number;
  user_discount_percent: number;
  cost_per_image: number;  // Credits from feature_catalog (image_generation_batch)
  cost_per_image_4k: number;  // Credits from feature_catalog (image_generation_batch_4k)
  poll_interval: number;
}

export interface BatchItem {
  prompt: string;
  aspect_ratio: string;
  seed?: number;
}

export interface BatchGenerateRequest {
  items: BatchItem[];
  template_id?: string;
  priority?: 'batch' | 'instant';
}

export interface BatchGenerateResponse {
  success: boolean;
  run_id?: string;
  batch_name?: string;
  reserved_credits?: number;
  total_images?: number;
  error?: string;
}

export interface BatchRun {
  id: string;
  status: 'pending' | 'submitted' | 'running' | 'completed' | 'failed' | 'cancelled';
  priority: string;
  gemini_batch_name: string | null;
  reserved_credits: number | null;
  progress_total: number;
  results: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  preview_image_url?: string | null;  // First completed image (presigned, 7 days expiry)
}

export interface BatchRunsResponse {
  success: boolean;
  runs: BatchRun[];
  error?: string;
}

export interface BatchStatusResponse {
  success: boolean;
  run_id: string;
  status: string;
  priority: string;
  batch_name: string | null;
  reserved_credits: number;
  results: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  error?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get batch configuration
 */
export async function getBatchConfig(): Promise<BatchConfig> {
  const response = await apiClient.get<{ success: boolean } & BatchConfig>(
    '/api/v1/batch/config'
  );
  return response.data;
}

/**
 * Create a new batch generation job
 */
export async function createBatch(data: BatchGenerateRequest): Promise<BatchGenerateResponse> {
  const response = await apiClient.post<BatchGenerateResponse>(
    '/api/v1/batch/generate',
    data
  );
  return response.data;
}

/**
 * List all batch runs for the current workspace
 */
export async function listBatchRuns(status?: string, limit?: number): Promise<BatchRun[]> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (limit) params.append('limit', limit.toString());

  const url = `/api/v1/batch/runs${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiClient.get<BatchRunsResponse>(url);
  return response.data.runs;
}

/**
 * Get status of a specific batch run
 */
export async function getBatchStatus(runId: string): Promise<BatchStatusResponse> {
  const response = await apiClient.get<BatchStatusResponse>(
    `/api/v1/batch/status/${runId}`
  );
  return response.data;
}

export interface BatchCancelResponse {
  success: boolean;
  refunded_credits?: number;
  message?: string;
  error?: string;
}

export interface BatchImage {
  id: string;
  sort_order: number;
  status: 'pending' | 'completed' | 'failed';
  output_url: string | null;
  s3_key: string | null;
  error_message: string | null;
}

export interface BatchImagesResponse {
  success: boolean;
  images: BatchImage[];
  stats: {
    total: number;
    completed: number;
    failed: number;
    pending: number;
  };
  error?: string;
}

/**
 * Cancel a batch run
 */
export async function cancelBatch(runId: string): Promise<BatchCancelResponse> {
  const response = await apiClient.post<BatchCancelResponse>(
    `/api/v1/batch/cancel/${runId}`
  );
  return response.data;
}

/**
 * Get images for a batch run
 * Presigned URLs are regenerated on-demand (they expire after 24h)
 */
export async function getBatchImages(runId: string): Promise<BatchImagesResponse> {
  const response = await apiClient.get<BatchImagesResponse>(
    `/api/v1/batch/images/${runId}`
  );
  return response.data;
}

export interface BatchPollResponse {
  success: boolean;
  status: string;
  gemini_status: string | null;
  completed_items: number;
  failed_items: number;
  credits_used: number;
  error: string | null;
  polled_at: string;
  message?: string;
}

/**
 * Manually poll a single batch for status updates
 * This checks Gemini API and processes results if completed
 */
export async function pollBatch(runId: string): Promise<BatchPollResponse> {
  const response = await apiClient.post<BatchPollResponse>(
    `/api/v1/batch/poll/${runId}`
  );
  return response.data;
}

// ============================================================================
// V2 Batch API - Extended with Mode Support
// ============================================================================

export type BatchMode = 'template' | 'collection' | 'prompt';

export interface BatchGenerateV2Request {
  files: File[];
  imageIds: string[];
  mode: BatchMode;
  templateId?: string;
  collectionId?: string;
  customPrompt?: string;
  variantsPerImage: number;
  aspectRatio: string;
  model?: string;
}

export interface BatchPreviewResponse {
  success: boolean;
  total_generations: number;
  cost_per_image: number;
  total_cost: number;
  discount_percent: number;
  breakdown: {
    input_images: number;
    variants_per_image: number | null;
    mode: BatchMode;
  };
  error?: string;
}

export interface WorkspaceImage {
  id: string;
  thumbnail_url: string | null;
  mime_type?: string;
  created_at?: string;
}

export interface WorkspaceImagesResponse {
  success: boolean;
  images: WorkspaceImage[];
  total: number;
  error?: string;
}

/**
 * Create a V2 batch generation with mode support
 */
export async function createBatchV2(data: BatchGenerateV2Request): Promise<BatchGenerateResponse> {
  const formData = new FormData();

  // Add files
  data.files.forEach((file) => {
    formData.append('files[]', file);
  });

  // Add workspace image IDs
  data.imageIds.forEach((id) => {
    formData.append('image_ids[]', id);
  });

  // Add mode and settings
  formData.append('mode', data.mode);
  formData.append('variants_per_image', data.variantsPerImage.toString());
  formData.append('aspect_ratio', data.aspectRatio);

  if (data.templateId) formData.append('template_id', data.templateId);
  if (data.collectionId) formData.append('collection_id', data.collectionId);
  if (data.customPrompt) formData.append('custom_prompt', data.customPrompt);
  if (data.model) formData.append('model', data.model);

  const response = await apiClient.post<BatchGenerateResponse>(
    '/api/v2/batch/generate/v2',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

/**
 * Preview batch cost without submitting
 */
export async function previewBatch(data: BatchGenerateV2Request): Promise<BatchPreviewResponse> {
  const formData = new FormData();

  data.files.forEach((file) => {
    formData.append('files[]', file);
  });

  data.imageIds.forEach((id) => {
    formData.append('image_ids[]', id);
  });

  formData.append('mode', data.mode);
  formData.append('variants_per_image', data.variantsPerImage.toString());
  formData.append('aspect_ratio', data.aspectRatio);

  if (data.templateId) formData.append('template_id', data.templateId);
  if (data.collectionId) formData.append('collection_id', data.collectionId);
  if (data.customPrompt) formData.append('custom_prompt', data.customPrompt);

  const response = await apiClient.post<BatchPreviewResponse>(
    '/api/v2/batch/preview',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

/**
 * List workspace images for batch selection
 */
export async function listWorkspaceImages(
  limit = 50,
  offset = 0
): Promise<WorkspaceImagesResponse> {
  const response = await apiClient.get<WorkspaceImagesResponse>(
    `/api/v2/batch/workspace-images?limit=${limit}&offset=${offset}`
  );
  return response.data;
}

// ============================================================================
// Matrix Batch Mode API
// ============================================================================

export interface MatrixItem {
  id: string; // client-side ID
  input_type: 'upload' | 'workspace' | 'asset';
  file_index?: number; // for uploads (legacy)
  image_id?: string; // for workspace images
  asset_id?: string; // for product asset images
  product_id?: string; // linked product (metadata only)
  product_name?: string; // display name in row
  mode?: BatchMode; // 'template' | 'collection' | 'prompt'
  template_id?: string; // optional - if not set, custom_prompt is used
  collection_id?: string; // for collection mode
  custom_prompt?: string; // used when template_id is not set
  aspect_ratios: string[];
  variants: number;
  seed?: number | null;
  // Output settings
  output_format?: 'jpeg' | 'png' | 'webp';
  output_quality?: number; // 1-100
  output_resolution?: 1024 | 2048 | 4096;
  // Client-side only (not sent to server)
  _file?: File;
  _thumbnailUrl?: string;
}

export interface MatrixConfig {
  matrix_items: MatrixItem[];
}

export interface MatrixPreviewResponse {
  success: boolean;
  total_generations: number;
  cost_per_image: number;
  total_cost: number;
  discount_percent: number;
  breakdown: {
    items: Array<{
      input_index: number;
      template_id: string;
      aspect_ratios: string[];
      variants: number;
      generations: number;
    }>;
  };
  error?: string;
}

export interface MatrixPreset {
  id: string;
  name: string;
  config: MatrixConfig;
  is_public: boolean;
  created_at: string;
}

export interface MatrixPresetsResponse {
  success: boolean;
  presets: MatrixPreset[];
  error?: string;
}

/**
 * Preview matrix batch cost without submitting
 */
export async function previewMatrixBatch(
  files: File[],
  imageIds: string[],
  config: MatrixConfig
): Promise<MatrixPreviewResponse> {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files[]', file);
  });

  imageIds.forEach((id) => {
    formData.append('image_ids[]', id);
  });

  formData.append('matrix_config', JSON.stringify(config));

  const response = await apiClient.post<MatrixPreviewResponse>(
    '/api/v2/batch/preview/matrix',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

/**
 * Generate matrix batch
 */
export async function generateMatrixBatch(
  files: File[],
  imageIds: string[],
  config: MatrixConfig
): Promise<BatchGenerateResponse> {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files[]', file);
  });

  imageIds.forEach((id) => {
    formData.append('image_ids[]', id);
  });

  formData.append('matrix_config', JSON.stringify(config));

  const response = await apiClient.post<BatchGenerateResponse>(
    '/api/v2/batch/generate/matrix',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

/**
 * Get saved matrix presets
 */
export async function getMatrixPresets(): Promise<MatrixPresetsResponse> {
  const response = await apiClient.get<MatrixPresetsResponse>(
    '/api/v2/batch/presets'
  );
  return response.data;
}

/**
 * Save a matrix preset
 */
export async function saveMatrixPreset(
  name: string,
  config: MatrixConfig,
  isPublic = false
): Promise<{ success: boolean; preset?: MatrixPreset; error?: string }> {
  const response = await apiClient.post<{ success: boolean; preset?: MatrixPreset; error?: string }>(
    '/api/v2/batch/presets',
    { name, config, is_public: isPublic }
  );
  return response.data;
}
